import {
  CommunicationContextType,
  CommunicationReceiverType,
  IAdHocCommunicationInput,
  IComposeContextSelection
} from '../../../lib/types/communication.types';
import {
  deliveryOptionRequiresEmail,
  deliveryOptionRequiresSMS
} from '../../../lib/helpers/communication.helpers';
import {
  describeInaccessibleAgeGroups,
  readInaccessibleAgeGroupIds
} from '../../../lib/helpers/age-group.helpers';
import { readUserInputMessage } from '../../../lib/helpers/graphql-error.helpers';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { resolveCommunicationContext } from '../AudienceSelector/audience-selector.helpers';
import { IComposePanelFields } from './compose-panel.types';
import { stripHtml } from './compose-panel.helpers';

interface IBuildAdHocInputArgs {
  values: IComposePanelFields;
  panelAssessmentId: string;
  test?: boolean;
  testEmails?: string[];
  testPhones?: string[];
  // Exemplar receiver a test send renders receiver-scoped tokens from.
  testSourceId?: string;
}

// Assembles the sendAdHocCommunication wire input shared by the real send and the
// test send — channel-gates content, resolves the most-specific context, strips HTML.
export const buildAdHocInput = ({
  values,
  panelAssessmentId,
  test = false,
  testEmails,
  testPhones,
  testSourceId
}: IBuildAdHocInputArgs): IAdHocCommunicationInput => {
  const usesEmail = deliveryOptionRequiresEmail(values.deliveryOption);
  const usesSMS = deliveryOptionRequiresSMS(values.deliveryOption);
  const { contextType, contextId } = resolveCommunicationContext(values.contexts, panelAssessmentId);

  const input: IAdHocCommunicationInput = {
    assessmentId: panelAssessmentId,
    deliveryOption: values.deliveryOption,
    title: values.title.trim(),
    subject: usesEmail ? values.subject : undefined,
    body: usesEmail ? stripHtml(values.bodyHtml) : undefined,
    bodyHtml: usesEmail ? values.bodyHtml : undefined,
    smsBody: usesSMS ? values.smsBody : undefined,
    // A test send empties the audience and delivers to the typed-in addresses;
    // a real send targets the selected audience.
    recipientCriteria: test ? {} : values.audience,
    contextType,
    contextId
  };

  if (test) {
    input.test = true;
    input.testEmails = testEmails;
    input.testPhones = testPhones;
    // Only when a source is picked — the server requires it iff the content uses
    // receiver-scoped tokens, and rejects an unsupported testSourceType.
    if (testSourceId) {
      input.testSourceType = CommunicationReceiverType.PLAYER_CONTACT;
      input.testSourceId = testSourceId;
    }
  }

  return input;
};

// A send refuses unless every age group it targets is still reachable, and names the ones
// it rejected. The chips carry their own labels, so a removed age group is still named.
export const describeSendRefusal = (
  error: unknown,
  contexts: IComposeContextSelection[]
): string | null => {
  const inaccessibleIds = readInaccessibleAgeGroupIds(error);
  if (!inaccessibleIds.length) return readUserInputMessage(error);

  const labelById = (id: string) => contexts
    .find((row) => row.type === CommunicationContextType.AGE_GROUP && row.id === id)?.label;
  const named = describeInaccessibleAgeGroups(inaccessibleIds, labelById);

  return `${named} ${pluralize(inaccessibleIds.length, 'is', 'are')} no longer available to message`
    + ` — remove ${pluralize(inaccessibleIds.length, 'it', 'them')} from the audience and try again.`;
};
